import { TipoEntrega } from '../dominio.js';
/**
 * A taxa de entrega tem três modos, escolhidos na tela /entrega do portal:
 *
 *  BAIRRO  — tabela de bairros com taxa fixa. Previsível, não depende de GPS.
 *  RAIO    — faixas concêntricas a partir da loja (até 2 km, até 4 km...).
 *            Depende de ter coordenada confiável do endereço.
 *  HIBRIDO — calcula por raio; sem coordenada confiável, cai para o bairro.
 *            É o padrão recomendado: o raio dá a precisão, o bairro dá a rede
 *            de segurança para quando o geocoding falha (e ele falha).
 */
export const ModoTaxa = { BAIRRO: 'BAIRRO', RAIO: 'RAIO', HIBRIDO: 'HIBRIDO' };
const PRECISOES_CONFIAVEIS = new Set(['ROOFTOP', 'RANGE_INTERPOLATED']);
export function distanciaConfiavel(destino) {
    return (destino.distanciaKm !== null &&
        Number.isFinite(destino.distanciaKm) &&
        destino.precisaoGeo !== null &&
        PRECISOES_CONFIAVEIS.has(destino.precisaoGeo));
}
/**
 * "12,0" — vírgula, não ponto.
 *
 * Esta frase vai inteira para a tela do CLIENTE no checkout e para a conversa
 * da IA no WhatsApp. "12.0 km" é como um computador escreve; quem lê é uma
 * pessoa em Caieiras.
 */
function kmEmPortugues(km) {
    return km.toFixed(1).replace('.', ',');
}
/** Menor faixa que cobre a distância. Faixas fora de ordem são toleradas. */
export function faixaPara(faixas, distanciaKm) {
    const ordenadas = [...faixas].sort((a, b) => a.ateKm - b.ateKm);
    return ordenadas.find((f) => distanciaKm <= f.ateKm) ?? null;
}
export function calcularTaxaEntrega(tipo, config, destino) {
    if (tipo !== TipoEntrega.ENTREGA) {
        return { ok: true, taxaCents: 0, tempoEstimadoMin: config.tempoPadraoMin, origem: 'SEM_TAXA' };
    }
    const porBairro = () => destino.bairro
        ? {
            ok: true,
            taxaCents: destino.bairro.taxaCents,
            tempoEstimadoMin: destino.bairro.tempoEstimadoMin,
            origem: 'BAIRRO',
        }
        : {
            ok: true,
            taxaCents: config.taxaPadraoCents,
            tempoEstimadoMin: config.tempoPadraoMin,
            origem: 'PADRAO',
        };
    if (config.modoTaxa === ModoTaxa.BAIRRO)
        return porBairro();
    const temDistancia = distanciaConfiavel(destino);
    if (!temDistancia) {
        if (config.modoTaxa === ModoTaxa.HIBRIDO)
            return porBairro();
        return {
            ok: false,
            codigo: 'ENDERECO_INSUFICIENTE',
            mensagem: 'Não foi possível localizar o endereço no mapa para calcular a taxa.',
        };
    }
    const distanciaKm = destino.distanciaKm;
    if (config.raioMaximoKm !== null && distanciaKm > config.raioMaximoKm) {
        return {
            ok: false,
            codigo: 'FORA_DE_AREA',
            mensagem: `Endereço a ${kmEmPortugues(distanciaKm)} km — fora da área de entrega.`,
        };
    }
    const faixa = faixaPara(config.faixas, distanciaKm);
    if (!faixa) {
        if (config.modoTaxa === ModoTaxa.HIBRIDO && destino.bairro)
            return porBairro();
        return {
            ok: false,
            codigo: 'FORA_DE_AREA',
            mensagem: `Endereço a ${kmEmPortugues(distanciaKm)} km — fora da área de entrega.`,
        };
    }
    return {
        ok: true,
        taxaCents: faixa.taxaCents,
        tempoEstimadoMin: faixa.tempoEstimadoMin,
        origem: 'RAIO',
        faixaAteKm: faixa.ateKm,
    };
}
/** Haversine. Serve de estimativa e de sanidade contra o Routes do Google. */
export function distanciaHaversineKm(a, b) {
    const R = 6371;
    const rad = (g) => (g * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
}
//# sourceMappingURL=entrega.js.map